import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TablePagination,
  Typography,
} from '@mui/material';
import AdminSurface from '../../../../components/admin/AdminSurface';
import auditService, { type ReceiptAudit } from '../../../../services/auditService';
import { adminColors, adminRadii, adminTypography } from '../../../../styles/adminDesignTokens';
import {
  adminButtonSx,
  adminButtonToneSx,
  adminCombineSx,
  adminEmptyStateSx,
} from '../../../../styles/adminComponentStyles';
import { FIX_OCR_STORE_ID_PARAM } from '../../../audit/fixOcr/utils/fixOcrQueryParams';

type StoreDetailsSalesReceiptsTabProps = {
  storeId: string;
};

const RECEIPTS_API_PAGE_SIZE = 200;
const DEFAULT_CLIENT_ROWS_PER_PAGE = 200;
const CLIENT_ROWS_PER_PAGE_OPTIONS = [200, 500, 1000];
const FINALIZED_RECEIPT_STATUSES = new Set(['finalized', 'auto_created', 'auto_finalized']);

type SalesLine = {
  bin?: number | string | null;
  game?: number | string | null;
  count?: number | string | null;
  amount?: number | string | null;
};

type DrawGameLine = {
  name?: string | null;
  count?: number | string | null;
  amount?: number | string | null;
};

const getFinalJson = (receipt: ReceiptAudit | null): any =>
  receipt?.parsed_data?.final_json ?? {};

const getJsonReportDate = (receipt: ReceiptAudit): string => {
  const finalJson = receipt.parsed_data?.final_json ?? {};
  return (
    receipt.parsed_data?.report_date
    || finalJson.report_date
    || finalJson.date
    || 'Unknown JSON date'
  );
};

const getJsonReportTime = (receipt: ReceiptAudit): string => {
  const finalJson = receipt.parsed_data?.final_json ?? {};
  return receipt.parsed_data?.report_time || finalJson.report_time || '';
};

const getJsonDateSortKey = (receipt: ReceiptAudit): string => {
  const reportDate = getJsonReportDate(receipt);
  const reportTime = getJsonReportTime(receipt);
  return `${reportDate} ${reportTime}`.trim();
};

const formatReportDate = (receipt: ReceiptAudit): string => {
  const reportDate = getJsonReportDate(receipt);
  if (reportDate === 'Unknown JSON date') {
    return reportDate;
  }

  const parsedDate = new Date(`${reportDate}T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) {
    return reportDate;
  }

  return parsedDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatReceiptTitle = (receipt: ReceiptAudit): string => {
  const reportDate = formatReportDate(receipt);
  const reportTime = getJsonReportTime(receipt);
  return `${reportDate}${reportTime ? ` ${reportTime}` : ''}`;
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return String(value);
};

const formatAmount = (value: unknown): string => {
  const numericValue = Number(value ?? 0);
  if (!Number.isFinite(numericValue)) {
    return '-';
  }
  return `$${numericValue.toFixed(2)}`;
};

const sortReceiptsByJsonDate = (left: ReceiptAudit, right: ReceiptAudit): number => {
  return getJsonDateSortKey(left).localeCompare(getJsonDateSortKey(right));
};

const isFinalizedSalesReceipt = (receipt: ReceiptAudit): boolean => (
  receipt.report_type === 'vending_sales'
  && FINALIZED_RECEIPT_STATUSES.has(String(receipt.status || '').toLowerCase())
);

const buildFixOcrUrl = (receiptId: string, storeId: string): string => {
  const params = new URLSearchParams();
  params.set(FIX_OCR_STORE_ID_PARAM, storeId);
  return `/dashboard/audit/fix-ocr/${receiptId}?${params.toString()}`;
};

const StoreDetailsSalesReceiptsTab: React.FC<StoreDetailsSalesReceiptsTabProps> = ({ storeId }) => {
  const [receipts, setReceipts] = useState<ReceiptAudit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_CLIENT_ROWS_PER_PAGE);

  const receiptsByDate = useMemo(
    () => [...receipts].sort(sortReceiptsByJsonDate),
    [receipts],
  );

  const paginatedReceipts = useMemo(
    () => receiptsByDate.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, receiptsByDate, rowsPerPage],
  );

  const loadReceipts = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    setError(null);
    try {
      const allReceipts: ReceiptAudit[] = [];
      let apiPage = 1;
      let total = 0;

      do {
        const response = await auditService.getReceiptAudits({
          store_id: storeId,
          status: 'finalized',
          report_type: 'vending_sales',
          page: apiPage,
          size: RECEIPTS_API_PAGE_SIZE,
        });

        const nextItems = response.items || [];
        allReceipts.push(...nextItems);
        total = response.total || allReceipts.length;
        apiPage += 1;
        if (nextItems.length === 0) {
          break;
        }
      } while (allReceipts.length < total);

      const finalizedSalesReceipts = allReceipts
        .filter(isFinalizedSalesReceipt)
        .sort(sortReceiptsByJsonDate);

      setReceipts(finalizedSalesReceipts);
      setPage(0);
    } catch (nextError: any) {
      setReceipts([]);
      setPage(0);
      setError(nextError?.response?.data?.detail || nextError?.message || 'Failed to load sales receipts');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    loadReceipts();
  }, [loadReceipts]);

  const handleChangePage = useCallback((_event: unknown, nextPage: number) => {
    setPage(nextPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  }, []);

  return (
    <Stack spacing={1.5}>
      <AdminSurface sx={{ p: 0, border:'0' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ ...adminTypography.sectionTitle, color: adminColors.text, fontSize: '20px' }}>
              Finalized Vending Sales Receipts
            </Typography>
            <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText, mt: 0.25 }}>
              Listed in ascending order by the date inside the receipt JSON. Finalized, auto-created, and auto-finalized vending sales reports are shown.
            </Typography>
          </Box>
          <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>
            {receiptsByDate.length} receipts
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={loadReceipts}
            disabled={loading}
            sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary)}
          >
            Refresh
          </Button>
        </Stack>
      </AdminSurface>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading ? (
        <AdminSurface sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </AdminSurface>
      ) : receiptsByDate.length === 0 ? (
        <Box sx={adminEmptyStateSx}>
          <Typography className="AdminEmptyState-title">No finalized sales receipts</Typography>
          <Typography className="AdminEmptyState-copy">No finalized vending sales receipts were found for this store.</Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {paginatedReceipts.map((receipt) => (
            <ReceiptCard key={receipt.id} receipt={receipt} storeId={storeId} />
          ))}
          <AdminSurface sx={{ px: 1 }}>
            <TablePagination
              component="div"
              count={receiptsByDate.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={CLIENT_ROWS_PER_PAGE_OPTIONS}
            />
          </AdminSurface>
        </Stack>
      )}
    </Stack>
  );
};

type ReceiptCardProps = {
  receipt: ReceiptAudit;
  storeId: string;
};

const ReceiptCard: React.FC<ReceiptCardProps> = ({ receipt, storeId }) => {
  const finalJson = getFinalJson(receipt);

  return (
    <AdminSurface sx={{ p: 1.5, overflow: 'hidden' }}>
      <Stack spacing={1.25}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              <Typography sx={{ ...adminTypography.sectionTitle, color: adminColors.text }}>
                {formatReceiptTitle(receipt)}
              </Typography>
              <StatusPill status={receipt.status} />
            </Stack>
            <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText, mt: 0.25 }}>
              Queue {receipt.id}
            </Typography>
          </Box>
          <Button
            component="a"
            href={buildFixOcrUrl(receipt.id, storeId)}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="contained"
            sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary)}
          >
            Open finalize page
          </Button>
        </Stack>

        <ReceiptHeader finalJson={finalJson} receipt={receipt} />

        <Divider />

        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={1.5} alignItems="stretch">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text, mb: 0.75 }}>
              Receipt Image
            </Typography>
            <ReceiptImage receiptId={receipt.id} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <FormattedSalesReceipt receipt={receipt} />
          </Box>
        </Stack>
      </Stack>
    </AdminSurface>
  );
};

const ReceiptImage: React.FC<{ receiptId: string }> = ({ receiptId }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let objectUrl: string | null = null;

    const loadImage = async () => {
      setImageLoading(true);
      try {
        const blob = await auditService.getAuditImage(receiptId);
        objectUrl = URL.createObjectURL(blob);
        if (isMounted) {
          setImageUrl(objectUrl);
        }
      } catch {
        if (isMounted) {
          setImageUrl(null);
        }
      } finally {
        if (isMounted) {
          setImageLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [receiptId]);

  return (
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: adminColors.surfaceSubtle,
        border: `1px solid ${adminColors.border}`,
        borderRadius: adminRadii.sm,
        display: 'flex',
        justifyContent: 'center',
        minHeight: 520,
        overflow: 'auto',
      }}
    >
      {imageLoading ? (
        <CircularProgress size={28} />
      ) : imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt="sales receipt"
          loading="lazy"
          sx={{ display: 'block', maxWidth: '100%', width: 'auto' }}
        />
      ) : (
        <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText }}>
          Image unavailable
        </Typography>
      )}
    </Box>
  );
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => (
  <Box
    component="span"
    sx={{
      backgroundColor: adminColors.successBackground,
      border: `1px solid ${adminColors.success}`,
      borderRadius: 999,
      color: adminColors.success,
      display: 'inline-flex',
      px: 1,
      py: 0.25,
      ...adminTypography.helper,
      textTransform: 'capitalize',
    }}
  >
    {status.replace(/_/g, ' ')}
  </Box>
);

type ReceiptHeaderProps = {
  finalJson: any;
  receipt: ReceiptAudit;
};

const ReceiptHeader: React.FC<ReceiptHeaderProps> = ({ finalJson, receipt }) => {
  const reportDate = formatReportDate(receipt);
  const reportTime = getJsonReportTime(receipt);
  const headerItems = [
    { label: 'JSON date', value: `${reportDate}${reportTime ? ` ${reportTime}` : ''}` },
    { label: 'Report', value: finalJson.report_name || 'Bin / Game Sales' },
    { label: 'Period', value: finalJson.report_period },
    { label: 'Printed at', value: finalJson.printed_at },
    { label: 'Lottery', value: finalJson.lottery },
    { label: 'Machine', value: finalJson.machine },
    { label: 'Location', value: finalJson.location_key },
    { label: 'Retailer', value: finalJson.retailer },
    { label: 'Terminal', value: finalJson.terminal },
  ];

  return (
    <Box
      sx={{
        backgroundColor: adminColors.surfaceSubtle,
        border: `1px solid ${adminColors.border}`,
        borderRadius: adminRadii.sm,
        p: 1,
      }}
    >
      <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text, mb: 0.75 }}>
        Receipt Header
      </Typography>
      <Box sx={{ display: 'grid', gap: 0.75, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        {headerItems.map((item) => (
          <Box key={item.label}>
            <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText }}>
              {item.label}
            </Typography>
            <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>
              {formatValue(item.value)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

type FormattedSalesReceiptProps = {
  receipt: ReceiptAudit;
};

const FormattedSalesReceipt: React.FC<FormattedSalesReceiptProps> = ({ receipt }) => {
  const finalJson = getFinalJson(receipt);
  const lines = Array.isArray(finalJson.lines) ? finalJson.lines as SalesLine[] : [];
  const drawGames = Array.isArray(finalJson.draw_games) ? finalJson.draw_games as DrawGameLine[] : [];
  const summary = finalJson.summary ?? {};

  return (
    <Box>
      <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text, mb: 0.75 }}>
        Formatted JSON
      </Typography>
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          border: `1px solid ${adminColors.border}`,
          borderRadius: adminRadii.sm,
          maxHeight: 680,
          overflow: 'auto',
          p: 1.5,
        }}
      >
        <Stack spacing={1.25}>
          <Box>
            <Typography sx={{ ...adminTypography.sectionTitle, color: adminColors.text }}>
              {formatValue(finalJson.report_name || 'Bin / Game Sales')}
            </Typography>
            <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText }}>
              {formatValue(finalJson.lottery)} · {formatValue(finalJson.machine)}
            </Typography>
            <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText }}>
              Retailer {formatValue(finalJson.retailer)} · Terminal {formatValue(finalJson.terminal)}
            </Typography>
            <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText }}>
              Report period: {formatValue(finalJson.report_period)}
            </Typography>
          </Box>

          <ReceiptLinesTable lines={lines} />

          {drawGames.length > 0 ? (
            <Box>
              <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text, mb: 0.75 }}>
                Draw Games
              </Typography>
              <Box component="table" sx={receiptTableSx}>
                <Box component="thead">
                  <Box component="tr">
                    <Box component="th">Name</Box>
                    <Box component="th">Count</Box>
                    <Box component="th">Amount</Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {drawGames.map((line, index) => (
                    <Box component="tr" key={`${line.name || 'draw'}-${index}`}>
                      <Box component="td">{formatValue(line.name)}</Box>
                      <Box component="td" sx={{ textAlign: 'right' }}>{formatValue(line.count)}</Box>
                      <Box component="td" sx={{ textAlign: 'right' }}>{formatAmount(line.amount)}</Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          ) : null}

          <Box sx={{ borderTop: `1px solid ${adminColors.border}`, pt: 1 }}>
            <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text, mb: 0.75 }}>
              Summary
            </Typography>
            <Box sx={{ display: 'grid', gap: 0.75, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <SummaryItem label="Scratchers" value={`${formatValue(summary.scratcher_count)} / ${formatAmount(summary.scratcher_amount)}`} />
              <SummaryItem label="Draw games" value={`${formatValue(summary.draw_game_count)} / ${formatAmount(summary.draw_game_amount)}`} />
              <SummaryItem label="Total" value={`${formatValue(summary.total_count)} / ${formatAmount(summary.total_amount)}`} />
              <SummaryItem label="Confidence" value={finalJson.confidence ? `${finalJson.confidence}%` : '-'} />
            </Box>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

const ReceiptLinesTable: React.FC<{ lines: SalesLine[] }> = ({ lines }) => (
  <Box>
    <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text, mb: 0.75 }}>
      Scratcher Lines
    </Typography>
    <Box component="table" sx={receiptTableSx}>
      <Box component="thead">
        <Box component="tr">
          <Box component="th">Bin</Box>
          <Box component="th">Game</Box>
          <Box component="th">Count</Box>
          <Box component="th">Amount</Box>
        </Box>
      </Box>
      <Box component="tbody">
        {lines.map((line, index) => (
          <Box component="tr" key={`${line.bin || 'bin'}-${line.game || 'game'}-${index}`}>
            <Box component="td">{formatValue(line.bin)}</Box>
            <Box component="td">{formatValue(line.game)}</Box>
            <Box component="td" sx={{ textAlign: 'right' }}>{formatValue(line.count)}</Box>
            <Box component="td" sx={{ textAlign: 'right' }}>{formatAmount(line.amount)}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

const SummaryItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ backgroundColor: adminColors.surfaceSubtle, borderRadius: adminRadii.sm, p: 1 }}>
    <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText }}>{label}</Typography>
    <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>{value}</Typography>
  </Box>
);

const receiptTableSx = {
  borderCollapse: 'collapse',
  width: '100%',
  '& th': {
    ...adminTypography.tableHeader,
    backgroundColor: adminColors.surfaceSubtle,
    borderBottom: `1px solid ${adminColors.border}`,
    color: adminColors.text,
    p: 0.75,
    textAlign: 'left',
  },
  '& td': {
    ...adminTypography.body,
    borderBottom: `1px solid ${adminColors.border}`,
    color: adminColors.text,
    p: 0.75,
  },
} as const;

export default StoreDetailsSalesReceiptsTab;
